import { IS_NATIVE } from './../../shared/platform.util';
import { FormBuilder, ReactiveFormsModule , Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { FirebaseCodeErrorService } from 'src/app/services/firebase-code-error.service';
import { LoadingService } from 'src/app/services/loading.service';
import { NetworkService } from 'src/app/services/network.service';


@Component({
    selector: 'app-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.css'],
    standalone: false
})
export class SignInComponent implements OnInit{
  private _authService = inject(AuthService);
  private _firebaseError = inject(FirebaseCodeErrorService);
  private router = inject(Router);
  private _route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private _loadingService = inject(LoadingService);
  private _network = inject(NetworkService);

  // Estado
  error = signal('');
  loading = computed(() => this._loadingService.isLoading());
  sessionError = computed(() => this._authService.sessionError());
  isOnline = computed(() => this._network.isOnline());
  // Si tu servicio expone un observable de éxito, lo uso directo con | async:
  success = toSignal(this._firebaseError.msgSuccess$, { initialValue: '' });
  showBioButton = signal(false);
  biometryKind = signal<'face' | 'finger' | null>(null);
  
  // Reactive form (tipado y nonNullable)
  loginUsuario = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    clave: ['', [Validators.required, Validators.minLength(6)]],
    recordarme: true,
  });

  fieldTextType = false;

  constructor(){}

  async ngOnInit(): Promise<void> {
    // Rehidrata por si el usuario ya tenía sesión nativa guardada
    try { await this._authService.ensureSession(); } catch {}
    if (!IS_NATIVE) return;

    // ¿Hay hardware biométrico? ¿Está habilitado por el usuario? ¿Qué tipo?
    const [available, enabled, kind, hasCreds] = await Promise.all([
      this._authService.isBiometricsAvailable(),
      this._authService.biometricsEnabled(),
      this._authService.getBiometryKind(),
      this._authService.hasBiometricCredentials(),
    ]);

    this.biometryKind.set(kind);

    // Mostrar botón solo si:
    // - es nativo
    // - hay hardware biométrico disponible
    // - el usuario lo habilitó
    // - y tenemos tokens previos (sirve para quick login sin pedir clave)
    this.showBioButton.set(!!(available && enabled && hasCreds));
  }

  async login() {
    // Evita doble submit si ya está cargando
    if (this.loading()) return;

    // Dispara validaciones en todos los campos
    this.loginUsuario.markAllAsTouched();
    if (this.loginUsuario.invalid) {
      this.error.set('Debe completar los campos requeridos');
      return;
    }

    const { email, clave, recordarme } = this.loginUsuario.getRawValue();

    try {
      const res = await this._loadingService.run(async () => {
        this.error.set('');
        return await this._authService.login(email, clave);
      });

      if (!res.ok && res.reason === 'email-not-verified') {
        // mantené el spinner prendido hasta terminar la navegación
        await this._loadingService.run(async () => {
          await this.router.navigate(['/verify-email'], { replaceUrl: true });
        });
        return;
      }
      // Recordarme centralizado en el servicio
      this._authService.setRememberMe(recordarme ? email : null, !!recordarme);

      // Tras el primer login nativo, ofrecer activar biometría si está disponible pero deshabilitada
      if (IS_NATIVE) {
        const [available, enabled] = await Promise.all([
          this._authService.isBiometricsAvailable(),
          this._authService.biometricsEnabled(),
        ]);
        if (available && !enabled) {
          const kind = await this._authService.getBiometryKind();
          const label = kind === 'face' ? 'Face ID' : 'huella';
          const ok = window.confirm(`¿Querés habilitar el ingreso con ${label}? Podrás entrar sin escribir la clave.`);
          if (ok) {
            await this._authService.saveBiometricCredentials(email, clave); // <— aquí
            await this._authService.enableBiometrics(true);
          }
        }
      }

      // Redirección: respetar ?redirect=… si vino del guard
      const redirect = this._route.snapshot.queryParamMap.get('redirect') ?? '/dashboard';
      await this.router.navigateByUrl(redirect);
    } catch (err: any) {
      localStorage.removeItem('recordarme');
      localStorage.removeItem('email');
      const code = this._firebaseError.extractCode(err);
      const msg = this._firebaseError.codeError(code) || 'Ocurrió un error al iniciar sesión.';
      this.error.set(msg);
      console.error(err);
    }
  }

  async loginWithBiometrics(): Promise<void> {
    if (!IS_NATIVE || this.loading()) return;

    try {
      await this._loadingService.run(async () => {
        await this._authService.biometricPasswordLogin(); // <— usa email/clave guardados
      });

      const redirect = this._route.snapshot.queryParamMap.get('redirect') ?? '/dashboard';
      await this.router.navigateByUrl(redirect);
    } catch (e) {
      console.error(e);
    }
  }

  async retry(): Promise<void> {
    if (this.loading()) return;
    await this._loadingService.run(async () => {
      await this._authService.retrySession();
    });
    if (this._authService.isLoggedIn()) {
      const redirect = this._route.snapshot.queryParamMap.get('redirect') ?? '/dashboard';
      await this.router.navigateByUrl(redirect);
    }
  }

  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }
}